import { create } from 'zustand';

export type ToastKind = 'error' | 'info' | 'success';

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  description?: string;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id'>) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

/// Store global de notificações. Pode ser usado direto (`useToastStore` em
/// componentes) ou via `queryClient` defaults (queryCache/mutationCache
/// onError em lib/queryClient.ts) — esse último captura qualquer mutation/
/// query que NÃO definiu `onError` próprio, evitando o sintoma "cliquei e
/// nada aconteceu".
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = nextId++;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
    // Auto-dismiss em 6s — tempo suficiente pra ler erro e copiar texto se
    // quiser reportar. Errors importantes devem ser persistidos via UI
    // local (banner inline) em vez de depender só do toast efêmero.
    setTimeout(() => {
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
    }, 6000);
    return id;
  },
  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/// Mostra o toast só se o anterior (o `id` que esta função devolveu na chamada
/// passada) já saiu da tela; senão devolve o mesmo id e não empilha. Para o
/// botão que explica no toque, onde toques repetidos e ansiosos (a tenda do
/// check-in) empilhavam avisos iguais (3º review da #1362).
export function toastOnce(previousId: number | null, show: () => number): number {
  if (previousId != null && useToastStore.getState().toasts.some((t) => t.id === previousId)) {
    return previousId;
  }
  return show();
}

/// Helpers atalho — não precisa importar `useToastStore` toda hora.
export const toast = {
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ kind: 'error', title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ kind: 'info', title, description }),
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ kind: 'success', title, description }),
};
